import type { Ring, Vec2 } from './types';

function distanceToSegment(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b[0] - a[0];
  const dy = b[1] - a[1];
  const lenSq = dx * dx + dy * dy;
  const t =
    lenSq > 0 ? Math.max(0, Math.min(1, ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq)) : 0;
  return Math.hypot(p[0] - (a[0] + t * dx), p[1] - (a[1] + t * dy));
}

function farthestBetween(chain: readonly Vec2[], first: number, last: number): [number, number] {
  let index = -1;
  let distance = -1;
  for (let i = first + 1; i < last; i++) {
    const d = distanceToSegment(chain[i]!, chain[first]!, chain[last]!);
    if (d > distance) [index, distance] = [i, d];
  }
  return [index, distance];
}

// Ramer–Douglas–Peucker with an explicit stack: every dropped vertex lies within tolerance of the
// chord that replaces it, so the result never strays further than tolerance from the chain.
function simplifyChain(chain: readonly Vec2[], tolerance: number): Vec2[] {
  const keep = new Array<boolean>(chain.length).fill(false);
  keep[0] = true;
  keep[chain.length - 1] = true;
  const stack: [number, number][] = [[0, chain.length - 1]];
  while (stack.length > 0) {
    const [first, last] = stack.pop()!;
    const [index, distance] = farthestBetween(chain, first, last);
    if (index < 0 || distance <= tolerance) continue;
    keep[index] = true;
    stack.push([first, index], [index, last]);
  }
  return chain.filter((_, i) => keep[i]);
}

// The ring is cut at its first vertex and the vertex farthest from it, so both anchors are corners
// that any faithful simplification keeps.
export function simplifyRing(ring: Ring, tolerance: number): Ring {
  if (ring.length <= 3) return ring;
  const origin = ring[0]!;
  const far = ring.reduce(
    (best, p, i) =>
      Math.hypot(p[0] - origin[0], p[1] - origin[1]) >
      Math.hypot(ring[best]![0] - origin[0], ring[best]![1] - origin[1])
        ? i
        : best,
    0
  );
  if (far === 0) return ring;
  const there = simplifyChain(ring.slice(0, far + 1), tolerance);
  const back = simplifyChain([...ring.slice(far), origin], tolerance);
  const simplified = [...there.slice(0, -1), ...back.slice(0, -1)];
  return simplified.length >= 3 ? simplified : ring;
}
